import {
  BILLING_STATUS,
  SECTION_STATUS,
  TypeScheduledItemRecord,
  TypeSubscriptionRecord,
} from '@/lib/api/subscriptionManager/types';
import { TypeUserRecord } from '@/lib/api/accountManager/types';
import { constructLegacyCustomerPortalLink } from '@/utils/shopSubdomain';
import {
  generateCancelSpecialSubscriptionZendeskLink,
  generateSkipPrepaidKitZendeskLink,
} from '@/utils/zendesk';
import { TypeAccountSettingsPageTranslations } from '@/utils/translations';
import { DisplayDateFormat, formatDateForDisplay } from '@/utils/dates';

export function _getActionLinks(
  t: TypeAccountSettingsPageTranslations,
  locale: string | undefined,
  user: TypeUserRecord,
  subscription: TypeSubscriptionRecord,
  nextUnprocessedItem?: TypeScheduledItemRecord,
) {
  const customerSkipKitLink =
    nextUnprocessedItem &&
    constructLegacyCustomerPortalLink(locale, [
      user.uuid,
      'account-settings',
      'skip',
      subscription.uuid,
      nextUnprocessedItem.uuid,
    ]);

  const reactivateSubscriptionLink = constructLegacyCustomerPortalLink(locale, [
    user.uuid,
    'account-settings',
    'reactivate',
    subscription.uuid,
  ]);

  const customerCancelSubscriptionLink = constructLegacyCustomerPortalLink(
    locale,
    [user.uuid, 'account-settings', 'cancel', subscription.uuid],
  );

  const removeBookBundleAddonLink = constructLegacyCustomerPortalLink(locale, [
    user.uuid,
    'account-settings',
    'cancel-add-on',
    'books_addon',
    subscription.uuid,
  ]);

  const cxSkipKitLink = generateSkipPrepaidKitZendeskLink(
    t.skipKitZendeskSubjectLine,
    locale,
  );
  const cxCancelSubscriptionLink = generateCancelSpecialSubscriptionZendeskLink(
    subscription,
    locale,
  );

  const editLanguageSettingsLink = constructLegacyCustomerPortalLink(locale, [
    user.uuid,
    'account-settings',
    'language',
    subscription.uuid,
  ]);

  const prepayAndSaveLink = constructLegacyCustomerPortalLink(locale, [
    user.uuid,
    'subscriptions',
    subscription.uuid,
    'prepay-extension',
  ]);

  const continueGiftLink = constructLegacyCustomerPortalLink(locale, [
    user.uuid,
    'subscriptions',
    subscription.uuid,
    'gift-extension',
  ]);

  return {
    customerSkipKitLink,
    cxSkipKitLink,
    reactivateSubscriptionLink,
    customerCancelSubscriptionLink,
    cxCancelSubscriptionLink,
    removeBookBundleAddonLink,
    editLanguageSettingsLink,
    prepayAndSaveLink,
    continueGiftLink,
  };
}

export function _getFormattedScheduledDate(
  scheduledItem: TypeScheduledItemRecord,
  locale: string | undefined,
) {
  return formatDateForDisplay(
    scheduledItem.scheduled_date,
    locale,
    DisplayDateFormat.ShortMonthAndDay,
  );
}

export function _getBillingDate(
  scheduledItem: TypeScheduledItemRecord,
  locale: string | undefined,
  prepaidLabel: string,
) {
  if (scheduledItem.billing_status === BILLING_STATUS.PREPAY) {
    return prepaidLabel;
  }

  return _getFormattedScheduledDate(scheduledItem, locale);
}

export function _isPrepaidBundle(scheduledItem?: TypeScheduledItemRecord) {
  if (scheduledItem?.billing_status === BILLING_STATUS.PREPAY) {
    return true;
  }

  return false;
}

export function _findChildForSubscription(
  subscription: TypeSubscriptionRecord,
  user: TypeUserRecord,
) {
  const childUuid = subscription?.child_uuid;
  return user.children?.find((child) => child.uuid === childUuid);
}

export function canPrepayForSubscription(
  subscription?: TypeSubscriptionRecord,
) {
  const activeSection = subscription?.schedule_sections?.find(
    (s) => s.section_status === SECTION_STATUS.ACTIVE,
  );

  const upcomingItems = activeSection?.scheduled_items?.filter(
    (i) =>
      !!i.scheduled_date &&
      i.billing_status === BILLING_STATUS.AWAITING_PAYMENT,
  );

  return upcomingItems && upcomingItems.length >= 2;
}
